import React, { useRef, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { toPng } from 'html-to-image';
import { saveAs } from 'file-saver';
import useDynamicQRStats from './hooks/useDynamicQRStats';
import './QRCodeResults.css';

function QRCodeResults({
  promptText,
  aiResponse,
  isProcessing,
  generatedQRCodes,
  currentSessionId,
  user,
  updateQRCode,
  deleteQRCode,
  dynamicQRHook
}) {
  const qrRefs = useRef({});
  const [copyToast, setCopyToast] = useState(false);

  const {
    dynamicQRData,
    editingUrl,
    newDestinationUrl,
    updateMessage,
    editingContent,
    editedContent,
    isDynamic,
    handleUpdateDestination,
    handleToggleStatus,
    startEditingUrl,
    cancelEditingUrl,
    startEditingContent,
    cancelEditingContent,
    updateEditedContent,
    updateNewDestinationUrl
  } = useDynamicQRStats(generatedQRCodes, dynamicQRHook);

  const downloadQRCode = async (qrId) => {
    const qrElement = qrRefs.current[qrId];
    if (!qrElement) return;
    try {
      const dataUrl = await toPng(qrElement, { quality: 1.0, pixelRatio: 2 });
      saveAs(dataUrl, `qrcode-${qrId}.png`);
    } catch (error) {
      // Silent fail
    }
  };

  const downloadQRCodeSVG = async (qrId) => {
    const qrElement = qrRefs.current[qrId];
    if (!qrElement) return;
    try {
      const canvas = qrElement.querySelector('canvas');
      const qr = generatedQRCodes.find(q => q.id === qrId);
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${qr.size}" height="${qr.size}" viewBox="0 0 ${qr.size} ${qr.size}">
        <rect width="${qr.size}" height="${qr.size}" fill="${qr.bgColor}"/>
        <image href="${canvas.toDataURL()}" width="${qr.size}" height="${qr.size}"/>
      </svg>`;
      const blob = new Blob([svg], { type: 'image/svg+xml' });
      saveAs(blob, `qrcode-${qrId}.svg`);
    } catch (error) {
      // Silent fail
    }
  };

  const copyQRContent = (content) => {
    navigator.clipboard.writeText(content)
      .then(() => {
        setCopyToast(true);
        setTimeout(() => setCopyToast(false), 2000);
      })
      .catch(() => {});
  };

  return (
    <div className="results-page">
      {copyToast && (
        <div className="toast-message toast-success">
          Copied to clipboard
        </div>
      )}

      <div className="results-container">
        {/* User Prompt */}
        <div className="prompt-display">
          <div className="prompt-avatar">
            {user?.email ? (
              <span className="user-initial">{user.email.charAt(0).toUpperCase()}</span>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </div>
          <div className="prompt-text">{promptText}</div>
        </div>

        {/* Main Content */}
        {isProcessing ? (
          <div className="processing-state">
            <div className="spinner-dots">
              <span></span>
              <span></span>
              <span></span>
            </div>
            <p>Generating your QR codes...</p>
          </div>
        ) : generatedQRCodes.length > 0 ? (
          <div className="qr-cards-container">
            {generatedQRCodes.map((qr, index) => (
              <div key={qr.id} className="qr-card">
                {/* LEFT SIDE - QR Visual */}
                <div className="qr-card-left">
                  <div className="qr-visual">
                    {/* Header with index and content preview */}
                    <div className="qr-content-header">
                      {/* FIXED: Changed from generatedQRCodes.length - index to index + 1 */}
                      <div className="qr-index-badge">
                        QR #{index + 1}
                      </div>

                      <div className="qr-content-info">
                        {qr.bulkDescription && (
                          <div className="qr-description">{qr.bulkDescription}</div>
                        )}

                        {/* Static QR Content */}
                        {!isDynamic(qr) && (
                          <div className="qr-content-preview">
                            {editingContent[qr.id] ? (
                              <div className="content-edit-inline">
                                <textarea
                                  className="content-textarea-inline"
                                  value={editedContent[qr.id] !== undefined ? editedContent[qr.id] : qr.qrContent}
                                  onChange={(e) => updateEditedContent(qr.id, e.target.value)}
                                  rows={3}
                                />
                                <div className="content-edit-actions">
                                  <button
                                    className="btn-save-inline"
                                    onClick={() => {
                                      updateQRCode(qr.id, { qrContent: editedContent[qr.id] });
                                      cancelEditingContent(qr.id, qr.qrContent);
                                    }}
                                  >
                                    Save
                                  </button>
                                  <button
                                    className="btn-cancel-inline"
                                    onClick={() => cancelEditingContent(qr.id, qr.qrContent)}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <>
                                <div className="content-text-preview">
                                  {qr.qrContent.length > 100
                                    ? qr.qrContent.substring(0, 100) + '...'
                                    : qr.qrContent}
                                </div>
                                <div className="content-actions">
                                  <button
                                    className="btn-action-small"
                                    onClick={() => startEditingContent(qr.id, qr.qrContent)}
                                    title="Edit content"
                                  >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                    Edit
                                  </button>
                                  <button
                                    className="btn-action-small"
                                    onClick={() => copyQRContent(qr.qrContent)}
                                    title="Copy content"
                                  >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                    Copy
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        )}

                        {/* Dynamic QR Destination URL */}
                        {isDynamic(qr) && dynamicQRData[qr.id] && (
                          <div className="qr-content-preview">
                            <div className="content-text-preview">
                              {dynamicQRData[qr.id].qrContent}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* QR Code Canvas */}
                    <div className="qr-code-wrapper" ref={(el) => (qrRefs.current[qr.id] = el)}>
                      <QRCodeCanvas
                        value={qr.qrContent}
                        size={qr.size}
                        bgColor={qr.bgColor}
                        fgColor={qr.fgColor}
                        level={qr.level}
                        includeMargin={true}
                      />
                    </div>

                    {/* Download Buttons */}
                    <div className="action-buttons">
                      <button className="btn-primary" onClick={() => downloadQRCode(qr.id)}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Download PNG
                      </button>
                      <button className="btn-secondary" onClick={() => downloadQRCodeSVG(qr.id)}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Download SVG
                      </button>
                    </div>

                    {/* Dynamic Stats */}
                    {isDynamic(qr) && dynamicQRData[qr.id] && (
                      <div className="dynamic-qr-info-compact">
                        <div className="dynamic-stats-row">
                          <div className="stat-item">
                            <span className="stat-number">{dynamicQRData[qr.id].scanCount}</span>
                            <span className="stat-label-small">Scans</span>
                          </div>
                          <div className="stat-item">
                            <span className={`status-indicator ${dynamicQRData[qr.id].isActive ? 'active' : 'inactive'}`}>
                              {dynamicQRData[qr.id].isActive ? 'Active' : 'Paused'}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* RIGHT SIDE - Controls */}
                <div className="qr-card-right">
                  <div className="qr-controls">
                    <div className="qr-control-header">
                      <span className="qr-number">Customization</span>
                    </div>

                    {/* Colors */}
                    <div className="control-item">
                      <label>Colors</label>
                      <div className="color-grid">
                        <div className="color-input">
                          <span>Background</span>
                          <input type="color" value={qr.bgColor} onChange={(e) => updateQRCode(qr.id, { bgColor: e.target.value })} />
                          <span className="hex">{qr.bgColor}</span>
                        </div>
                        <div className="color-input">
                          <span>Foreground</span>
                          <input type="color" value={qr.fgColor} onChange={(e) => updateQRCode(qr.id, { fgColor: e.target.value })} />
                          <span className="hex">{qr.fgColor}</span>
                        </div>
                      </div>
                    </div>

                    {/* Size */}
                    <div className="control-item">
                      <label><span>Size</span><span className="value">{qr.size}px</span></label>
                      <input
                        type="range"
                        min="128"
                        max="512"
                        value={qr.size}
                        onChange={(e) => updateQRCode(qr.id, { size: parseInt(e.target.value) })}
                      />
                    </div>

                    {/* Error Correction */}
                    <div className="control-item">
                      <label>Error Correction</label>
                      <select value={qr.level} onChange={(e) => updateQRCode(qr.id, { level: e.target.value })}>
                        <option value="L">Low (7%)</option>
                        <option value="M">Medium (15%)</option>
                        <option value="Q">Quartile (25%)</option>
                        <option value="H">High (30%)</option>
                      </select>
                    </div>

                    {/* Dynamic QR Controls */}
                    {isDynamic(qr) && dynamicQRData[qr.id] && (
                      <div className="dynamic-qr-controls">
                        <div className="controls-header">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path d="M12 20h9M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          <span>Dynamic Settings</span>
                        </div>

                        {updateMessage[qr.id] && (
                          <div className={`update-message ${updateMessage[qr.id].type}`}>
                            {updateMessage[qr.id].text}
                          </div>
                        )}

                        <div className="control-item">
                          <label>Destination URL</label>
                          {editingUrl[qr.id] ? (
                            <div className="url-edit-mode">
                              <input
                                type="url"
                                className="url-input"
                                placeholder="https://example.com"
                                defaultValue={dynamicQRData[qr.id].qrContent}
                                onChange={(e) => updateNewDestinationUrl(qr.id, e.target.value)}
                              />
                              <div className="url-buttons">
                                <button className="btn-save" onClick={() => handleUpdateDestination(qr.id)}>Save</button>
                                <button className="btn-cancel" onClick={() => cancelEditingUrl(qr.id)}>Cancel</button>
                              </div>
                            </div>
                          ) : (
                            <div className="url-display-mode">
                              <div className="current-url">{dynamicQRData[qr.id].qrContent}</div>
                              <button className="btn-edit" onClick={() => startEditingUrl(qr.id, dynamicQRData[qr.id].qrContent)}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                Edit URL
                              </button>
                            </div>
                          )}
                        </div>

                        <div className="control-item">
                          <label>QR Status</label>
                          <div className="status-toggle">
                            <button
                              className={`status-option ${dynamicQRData[qr.id].isActive ? 'active' : ''}`}
                              onClick={() => handleToggleStatus(qr.id, !dynamicQRData[qr.id].isActive)}
                              disabled={dynamicQRData[qr.id].isActive}
                            >
                              <span className="status-radio"></span>Active
                            </button>
                            <button
                              className={`status-option ${!dynamicQRData[qr.id].isActive ? 'active' : ''}`}
                              onClick={() => handleToggleStatus(qr.id, !dynamicQRData[qr.id].isActive)}
                              disabled={!dynamicQRData[qr.id].isActive}
                            >
                              <span className="status-radio"></span>Paused
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Delete Button (bulk only) */}
                {generatedQRCodes.length > 1 && (
                  <button className="btn-delete-compact" onClick={() => deleteQRCode(qr.id)} title="Delete this QR">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-icon">QR</div>
            <p>No QR codes generated yet</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default QRCodeResults